/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from 'react-intl';

import {useProductSearch} from '@salesforce/commerce-sdk-react';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer';
import {trackSearch, useTrackSelectItem, VIEW_ITEM_LIST} from '@amblique/king-living/app/analytics/ga4';
import {SearchIcon, SubmitSearchIcon} from '@amblique/king-living/app/components/icons';
import {
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  IconButton,
  Input,
  useMediaQuery,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';
import {productUrlBuilder, searchUrlBuilder} from '@amblique/king-living/app/utils/url';
import {saveRecentSearch} from '@amblique/king-living/app/utils/search-recent';
import {ensureArray} from '@amblique/king-living/app/utils/utils';

import {useContentfulHeader} from '@amblique/king-living/app/hooks/use-contentful-header';
import {useBlogPagesByKeywordSearch} from '@amblique/king-living/app/contentful/hooks';
import SearchIdlePanel from '@amblique/king-living/app/components/search/partials/search-idle-panel';
import SearchRecommendedForYouColumn from '@amblique/king-living/app/components/search/partials/search-recommended-for-you-column';
import SearchTypeaheadPanel from '@amblique/king-living/app/components/search/partials/search-typeahead-panel';
import {
  SEARCH_TYPEAHEAD_PRODUCT_HTYPE_REFINE,
  buildTypeaheadRowsFromBlogPages,
  buildTypeaheadRowsFromHits,
  countProductRowsInList,
  mergeAndCapTypeaheadRows,
} from '@amblique/king-living/app/components/search/search-typeahead-helpers';

/** Minimum characters before SCAPI typeahead runs; below this shows Search Idle (Contentful). */
const SEARCH_TYPEAHEAD_MIN_CHARS = 3;

const TYPEAHEAD_EXPAND = Object.freeze([
  'promotions',
  'variations',
  'prices',
  'images',
  'page_meta_tags',
  'custom_properties'
]);

/**
 * Search field for the header modal: Search Idle below 3 chars, SCAPI typeahead at 3+.
 *
 * @param {object} props
 * @param {import('react').RefObject<HTMLInputElement>} [props.inputRef]
 * @param {() => void} [props.onCloseModal]
 */
const Search = ({onCloseModal, inputRef, ...props}) => {
  const intl = useIntl();
  const navigate = useNavigation();
  const {data: customer} = useCurrentCustomer();
  const {site} = useMultiSite();
  const siteId = site?.id;

  const {
    data: contentfulHeader,
    isLoading: isHeaderLoading,
  } = useContentfulHeader(siteId, {
    enabled: Boolean(siteId),
  });

  const searchIdle = contentfulHeader?.searchIdle;
  const searchTyping = contentfulHeader?.searchTyping;

  const [liveInput, setLiveInput] = useState('');
  const [debouncedInput, setDebouncedInput] = useState('');
  const internalRef = useRef(null);
  const searchInputRef = inputRef || internalRef;

  const [isMobile] = useMediaQuery('(max-width: 991px)');

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedInput(liveInput);
    }, 300);

    return () => window.clearTimeout(handle);
  }, [liveInput]);

  const liveTrimmed = liveInput.trim();
  const debouncedTrimmed = debouncedInput.trim();
  const showIdlePanel = liveTrimmed.length < SEARCH_TYPEAHEAD_MIN_CHARS;
  const trackSelectItem = useTrackSelectItem();

  const productSearchParameters = useMemo(
    () => ({
      q: debouncedTrimmed,
      limit: 12,
      perPricebook: true,
      expand: [...TYPEAHEAD_EXPAND],
      refine: [SEARCH_TYPEAHEAD_PRODUCT_HTYPE_REFINE],
    }),
    [debouncedTrimmed]
  );

  const typeaheadEnabled =
    !showIdlePanel &&
    debouncedTrimmed.length >= SEARCH_TYPEAHEAD_MIN_CHARS;

  const {
    data: productSearchData,
    isFetching: isProductSearchFetching,
    isLoading: isProductSearchLoading,
  } = useProductSearch(
    {parameters: productSearchParameters},
    {
      enabled: typeaheadEnabled,
      keepPreviousData: true,
    }
  );

  const {
    data: blogSearchCollection,
    isFetching: isBlogSearchFetching,
    isLoading: isBlogSearchLoading,
    isError: isBlogSearchError,
  } = useBlogPagesByKeywordSearch(debouncedTrimmed, {
    limit: 4,
    queryOptions: {
      enabled: typeaheadEnabled && !isMobile,
    },
  });

  const blogSearchItems = isBlogSearchError ? [] : blogSearchCollection?.items || [];

  const productTagLabel = intl.formatMessage({
    id: 'search.idle.product_tag',
    defaultMessage: 'Product',
  });

  const blogTagLabel = intl.formatMessage({
    id: 'search.typeahead.blog_tag',
    defaultMessage: 'Blog',
  });

  const displayRows = useMemo(() => {
    const hits = productSearchData?.hits || [];
    const productBuilt = buildTypeaheadRowsFromHits(hits, debouncedTrimmed).map((row) => {
      const category = String(row.primaryCategoryName ?? '').trim();
      const tag =
        row.kind === 'product' && category ?
          `${productTagLabel} / ${category}` :
          productTagLabel;

      return {
        ...row,
        tag,
        onSelect: () => {
          void trackSelectItem({
            index: row.productIndex,
            listMeta: VIEW_ITEM_LIST.QUICK_SEARCH,
            product: row.product,
          });

          if (onCloseModal) {
            onCloseModal();
          }

          navigate(row.link);
        },
      };
    });

    const blogBuilt = isMobile ?
      [] :
      buildTypeaheadRowsFromBlogPages(blogSearchItems, debouncedTrimmed).map(
        (row) => ({
          ...row,
          tag: blogTagLabel,
          onSelect: () => {
            if (onCloseModal) {
              onCloseModal();
            }

            navigate(row.link);
          },
        })
      );

    return mergeAndCapTypeaheadRows(blogBuilt, productBuilt, isMobile);
  }, [
    blogSearchItems,
    blogTagLabel,
    debouncedTrimmed,
    isMobile,
    navigate,
    onCloseModal,
    productSearchData?.hits,
    productTagLabel,
    trackSelectItem
  ]);

  const visibleProductRowCount = useMemo(
    () => countProductRowsInList(displayRows),
    [displayRows]
  );

  const viewAllProductTotal = useMemo(() => {
    const total = productSearchData?.total;

    return typeof total === 'number' ? total : undefined;
  }, [productSearchData?.total]);

  const isTypeaheadPending =
    typeaheadEnabled &&
    (isProductSearchLoading ||
      isProductSearchFetching ||
      (!isMobile && (isBlogSearchLoading || isBlogSearchFetching)));

  const hasDisplayableResults = displayRows.length > 0;

  const showNoResultsSuggestions = useMemo(() => {
    return (
      !showIdlePanel &&
      liveTrimmed.length >= SEARCH_TYPEAHEAD_MIN_CHARS &&
      debouncedTrimmed === liveTrimmed &&
      !isTypeaheadPending &&
      !hasDisplayableResults
    );
  }, [
    debouncedTrimmed,
    hasDisplayableResults,
    isTypeaheadPending,
    liveTrimmed.length,
    showIdlePanel,
  ]);

  const hasSettledSearchNoResults = useCallback(
    (query) => {
      const trimmed = String(query || '').trim();

      if (trimmed.length < SEARCH_TYPEAHEAD_MIN_CHARS) {
        return false;
      }

      if (trimmed !== debouncedTrimmed || isTypeaheadPending) {
        return false;
      }

      if (!productSearchData) {
        return false;
      }

      const total = productSearchData.total;

      if (typeof total === 'number') {
        return total === 0;
      }

      return !(productSearchData.hits?.length > 0);
    },
    [debouncedTrimmed, isTypeaheadPending, productSearchData]
  );

  const searchTypingArticleTagCount = useMemo(() => {
    return ensureArray(searchTyping?.articleTags)
      .map((t) => String(t).trim())
      .filter(Boolean).length;
  }, [searchTyping?.articleTags]);

  const showRecommendedForYouDesktop =
    !isMobile &&
    !showIdlePanel &&
    hasDisplayableResults &&
    !isTypeaheadPending &&
    searchTypingArticleTagCount > 0;

  const clearInput = useCallback(() => {
    setLiveInput('');
    setDebouncedInput('');

    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  }, [searchInputRef]);

  const onSubmitSearch = useCallback(
    (e) => {
      e.preventDefault();
      const searchText = (searchInputRef.current?.value ?? liveInput).trim();

      if (searchText.length < 1) {
        return;
      }

      saveRecentSearch(searchText);

      if (searchText.length >= SEARCH_TYPEAHEAD_MIN_CHARS) {
        if (searchText !== liveInput) {
          setLiveInput(searchText);
        }

        if (searchText !== debouncedInput) {
          setDebouncedInput(searchText);
          return;
        }

        if (isTypeaheadPending) {
          return;
        }

        if (hasSettledSearchNoResults(searchText)) {
          void trackSearch({customer, searchTerm: searchText});
          return;
        }
      }

      clearInput();

      if (onCloseModal) {
        onCloseModal();
      }

      void trackSearch({customer, searchTerm: searchText});
      navigate(searchUrlBuilder(searchText));
    },
    [
      clearInput,
      customer,
      debouncedInput,
      hasSettledSearchNoResults,
      isTypeaheadPending,
      liveInput,
      navigate,
      onCloseModal,
      saveRecentSearch,
      searchInputRef,
    ]
  );

  const handlePickPopularSearch = useCallback(
    (term) => {
      const t = String(term || '').trim();

      if (!t) {
        return;
      }

      setLiveInput(t);
      setDebouncedInput(t);

      if (searchInputRef.current) {
        searchInputRef.current.value = t;
      }

      searchInputRef.current?.focus();
    },
    [searchInputRef]
  );

  const handlePickBlog = useCallback(
    (blog, {skipNavigate = false} = {}) => {
      const path = String(blog?.pageSlug || '').trim();

      if (!path) {
        return;
      }

      if (onCloseModal) {
        onCloseModal();
      }

      if (!skipNavigate) {
        navigate(path);
      }
    },
    [navigate, onCloseModal]
  );

  const handlePickProduct = useCallback(
    (product, index) => {
      if (typeof index === 'number') {
        void trackSelectItem({
          index,
          listMeta: VIEW_ITEM_LIST.QUICK_SEARCH,
          product,
        });
      }

      const link = productUrlBuilder(product);

      if (!link) {
        return;
      }

      if (onCloseModal) {
        onCloseModal();
      }

      navigate(link);
    },
    [navigate, onCloseModal, trackSelectItem]
  );

  const handleViewAllResults = useCallback(() => {
    const q = debouncedTrimmed;

    if (!q) {
      return;
    }

    saveRecentSearch(q);
    clearInput();

    if (onCloseModal) {
      onCloseModal();
    }

    void trackSearch({customer, searchTerm: q});
    navigate(searchUrlBuilder(q));
  }, [clearInput, customer, debouncedTrimmed, navigate, onCloseModal, saveRecentSearch]);

  const styles = useMultiStyleConfig('SearchForm');

  const placeholder = intl.formatMessage({
    id: 'search.field.placeholder',
    defaultMessage: 'Search King',
  });

  const submitSearchAriaLabel = intl.formatMessage({
    id: 'search.action.submit.aria_label',
    defaultMessage: 'Submit search',
  });

  const searchFieldAriaLabel = intl.formatMessage({
    id: 'search.field.aria_label',
    defaultMessage: 'Search King',
  });

  const handleInputChange = useCallback((e) => {
    setLiveInput(e.target.value);
  }, []);

  return (
    <Box>
      <form onSubmit={onSubmitSearch}>
        <Box>
          <HStack {...styles.inputContainer}>
            <Box {...styles.iconWrapper}>
              <SearchIcon />
            </Box>
            <Input
              ref={searchInputRef}
              aria-label={searchFieldAriaLabel}
              autoComplete="off"
              id="search-input"
              placeholder={placeholder}
              type="search"
              value={liveInput}
              onChange={handleInputChange}
              {...styles.input}
              {...props}
            />
            {liveTrimmed.length > 0 && (
              <HStack {...styles.actionsContainer}>
                <Button
                  type="button"
                  onClick={() => {
                    clearInput();
                  }}
                  {...styles.clearButton}
                >
                  <FormattedMessage
                    defaultMessage="Clear"
                    id="search.action.clear"
                  />
                </Button>
                <IconButton
                  aria-label={submitSearchAriaLabel}
                  data-testid="search-submit-button"
                  icon={<SubmitSearchIcon {...styles.submitButtonIcon} />}
                  sx={styles.submitButton}
                  type="submit"
                  variant="unstyled"
                />
              </HStack>
            )}
          </HStack>
          <Divider {...styles.divider} />
        </Box>
      </form>

      {showIdlePanel ? (
        <SearchIdlePanel
          isHeaderLoading={isHeaderLoading}
          searchIdle={searchIdle}
          onPickBlog={handlePickBlog}
          onPickPopularSearch={handlePickPopularSearch}
          onPickProduct={handlePickProduct}
        />
      ) : showRecommendedForYouDesktop ? (
        <Flex
          align="flex-start"
          gap={{base: 0, md: 10}}
          justify="space-between"
          marginTop={6}
          minW={0}
          width="full"
        >
          <Box flex={1} maxW={{base: 'full', md: '619px'}} minW={0}>
            <SearchTypeaheadPanel
              debouncedQueryTrimmed={debouncedTrimmed}
              displayRows={displayRows}
              hasDisplayableResults={hasDisplayableResults}
              isPanelLoading={isTypeaheadPending}
              liveQueryTrimmed={liveTrimmed}
              marginTop={0}
              minQueryLength={SEARCH_TYPEAHEAD_MIN_CHARS}
              viewAllProductTotal={viewAllProductTotal}
              visibleProductRowCount={visibleProductRowCount}
              onViewAllResults={handleViewAllResults}
            />
          </Box>
          <SearchRecommendedForYouColumn
            isHeaderLoading={isHeaderLoading}
            searchTyping={searchTyping}
            onPickBlog={handlePickBlog}
          />
        </Flex>
      ) : (
        <>
          <SearchTypeaheadPanel
            debouncedQueryTrimmed={debouncedTrimmed}
            displayRows={displayRows}
            hasDisplayableResults={hasDisplayableResults}
            isPanelLoading={isTypeaheadPending}
            liveQueryTrimmed={liveTrimmed}
            minQueryLength={SEARCH_TYPEAHEAD_MIN_CHARS}
            viewAllProductTotal={viewAllProductTotal}
            visibleProductRowCount={visibleProductRowCount}
            onViewAllResults={handleViewAllResults}
          />
          {showNoResultsSuggestions && (isHeaderLoading || searchIdle) ? (
            <Box
              data-testid="search-overlay-no-results-suggestions"
              marginTop={{base: 6, md: 8}}
              width="100%"
            >
              <SearchIdlePanel
                isHeaderLoading={isHeaderLoading}
                searchIdle={searchIdle}
                onPickBlog={handlePickBlog}
                onPickPopularSearch={handlePickPopularSearch}
                onPickProduct={handlePickProduct}
              />
            </Box>
          ) : null}
        </>
      )}
    </Box>
  );
};

Search.displayName = 'SearchInput';

Search.propTypes = {
  inputRef: PropTypes.shape({current: PropTypes.any}),
  onCloseModal: PropTypes.func,
};

export default Search;
